/**
 * Persistent header nav - visually/structurally matches workers/agent-api/
 * src/pageChrome.js's buildNav() (same tab set: Home / Properties / custom
 * pages / About), so switching between a custom page (e.g.
 * "Canadians") and the map ("Properties", this app) feels like one site
 * instead of two. Links are always absolute (https://<subdomain>.
 * slowmaui.com/...), never relative - this same SPA build is served at
 * both the bare apex and every agent subdomain (see workers/agent-site's
 * handleApex() vs handleGet()), and custom pages/About only exist on the
 * subdomain, so a relative link clicked from the apex would silently 404.
 *
 * Contact/Login stay as the existing in-page buttons (#contactBtn/
 * #userAccountBtn) - those are actions on modals already on THIS page, not
 * navigation, so no absolute-URL concern there.
 */
import type { DataClient } from '@slowmaui/api-client';

export interface PersistentHeaderOptions {
  api: DataClient;
  subdomain: string;
}

export async function initPersistentHeader(options: PersistentHeaderOptions): Promise<void> {
  const container = document.getElementById('persistent-nav-tabs');
  if (!container) return;

  const base = `https://${options.subdomain}.slowmaui.com`;

  // Render the one tab we always know about immediately (no flash of
  // empty nav while the fetch is in flight), then fill in the rest.
  container.innerHTML = `<a href="${base}/properties" class="active">Properties</a>`;

  let pages: Array<{ slug: string; navLabel: string; isHome: boolean }> = [];
  try {
    const result = await options.api.getAgentPages(options.subdomain);
    pages = result.pages || [];
  } catch (error) {
    console.error('[Header] Failed to load agent pages:', error);
  }

  renderTabs(container, base, pages);
}

function renderTabs(
  container: HTMLElement,
  base: string,
  pages: Array<{ slug: string; navLabel: string; isHome: boolean }>
): void {
  const homeEntry = pages.find((p) => p.isHome);
  const homeLabel = homeEntry?.navLabel || 'Home';

  let html = `<a href="${base}/">${escHeaderHtml(homeLabel)}</a>`;
  html += `<a href="${base}/properties" class="active">Properties</a>`;

  pages.filter((p) => !p.isHome).forEach((p) => {
    html += `<a href="${base}/${escHeaderHtml(p.slug)}">${escHeaderHtml(p.navLabel)}</a>`;
  });

  container.innerHTML = html;
}

function escHeaderHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
